import React from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { ArrowLeft, Rocket, Construction } from 'lucide-react';
import { cn } from '@/lib/utils';
import GradientButton from '@/components/ui/GradientButton';

interface ComingSoonModalProps {
  isOpen: boolean;
  onBack: () => void;
  title?: string;
  description?: string;
  featureName?: string;
}

const ComingSoonModal: React.FC<ComingSoonModalProps> = ({
  isOpen,
  onBack,
  title = "Coming Soon",
  description,
  featureName = "This feature"
}) => {
  const defaultDescription = `${featureName} is currently under development and will be available soon. We're working hard to bring you an amazing experience!`;

  return (
    <AnimatePresence>
      {isOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center">
          {/* Semi-transparent backdrop */}
          <motion.div
            className="absolute inset-0 bg-black/60 backdrop-blur-sm"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.2 }}
          />
          
          {/* Modal content */}
          <motion.div
            className={cn(
              "relative w-full max-w-md mx-4 bg-background rounded-lg shadow-lg border border-border",
              "p-6 space-y-6"
            )}
            initial={{ opacity: 0, scale: 0.95, y: 20 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            exit={{ opacity: 0, scale: 0.95, y: 20 }}
            transition={{ 
              type: "spring", 
              stiffness: 300, 
              damping: 25,
              duration: 0.3 
            }}
          >
            {/* Header with icons */}
            <div className="text-center space-y-4">
              <div className="flex items-center justify-center space-x-2">
                <div className="relative">
                  <Rocket className="h-8 w-8 text-blue-500" />
                  <Construction className="h-4 w-4 text-orange-500 absolute -bottom-1 -right-1" />
                </div>
              </div>
              
              <div>
                <h2 className="text-2xl font-bold text-foreground mb-2">
                  {title}
                </h2>
                <div className="w-16 h-1 bg-gradient-to-r from-blue-500 to-purple-500 rounded-full mx-auto" />
              </div>
            </div>

            {/* Content */}
            <div className="text-center space-y-4">
              <p className="text-muted-foreground leading-relaxed">
                {description || defaultDescription}
              </p>
              
              <div className="bg-muted/30 rounded-lg p-4 border border-border/50">
                <p className="text-sm text-muted-foreground">
                  <span className="font-medium text-foreground">Stay tuned!</span> We're putting the finishing touches on this feature to ensure it meets our high standards.
                </p>
              </div>
            </div>

            {/* Back button in bottom left */}
            <div className="flex justify-start">
              <GradientButton
                variant="default"
                icon={<ArrowLeft className="h-4 w-4" />}
                onClick={onBack}
                className="min-w-[120px]"
              >
                Go Back
              </GradientButton>
            </div>
          </motion.div>
        </div>
      )}
    </AnimatePresence>
  );
};

export default ComingSoonModal; 